import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DashboardShell } from "@/components/DashboardShell";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, CreditCard, FileText, LayoutDashboard, Loader2, Plus, RefreshCw, Search, Users } from "lucide-react";
import { toast } from "sonner";

const NAV = [
  { key: "home", label: "Overview", to: "/admin", icon: LayoutDashboard },
  { key: "users", label: "Users & Papers", to: "/admin/users", icon: Users },
  { key: "plans", label: "Purchase Plans", to: "/admin/plans", icon: CreditCard },
  { key: "papers", label: "Question Papers", to: "/admin/papers", icon: FileText },
  { key: "failed", label: "Failed evaluations", to: "/admin/failed-evaluations", icon: AlertTriangle },
];

export default function UserPurchases() {
  const [users, setUsers] = useState([]);
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [pending, setPending] = useState(null);
  const [saving, setSaving] = useState(false);
  const [assigningUser, setAssigningUser] = useState(null);
  const [assignment, setAssignment] = useState({ paperId: "", quantity: 1 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: userData }, { data: paperData }] = await Promise.all([
        api.get("/admin/users/papers"),
        api.get("/question-papers"),
      ]);
      setUsers(userData);
      setPapers(paperData.filter((paper) => paper.active !== false && paper.question_paper_pdf));
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Could not load users and purchases");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return users;
    return users.filter((user) => (
      [user.name, user.email, user.phone, user.board, user.medium]
        .some((value) => String(value || "").toLowerCase().includes(term))
      || user.purchased_papers.some((paper) =>
        [paper.title, paper.subject].some((value) => String(value || "").toLowerCase().includes(term)))
    ));
  }, [search, users]);

  const deletePaperAccess = async () => {
    if (!pending) return;
    setSaving(true);
    try {
      const { data } = await api.delete(`/admin/users/${pending.user.id}/papers/${pending.paper.id}`);
      toast.success(data.message || "Student paper access removed");
      setPending(null);
      await load();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Could not remove student paper access");
    } finally {
      setSaving(false);
    }
  };

  const openAssignment = (user) => {
    setAssigningUser(user);
    setAssignment({ paperId: "", quantity: 1 });
  };

  const assignPaper = async () => {
    if (!assigningUser || !assignment.paperId) {
      toast.error("Select a question paper");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(
        `/admin/users/${assigningUser.id}/papers/${assignment.paperId}/assign`,
        { quantity: Number(assignment.quantity) },
      );
      toast.success(data.message || "Question paper assigned");
      setAssigningUser(null);
      await load();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Could not assign the question paper");
    } finally {
      setSaving(false);
    }
  };

  const paperCount = users.reduce((total, user) => total + user.purchased_papers.length, 0);

  return (
    <DashboardShell nav={NAV} title="Users & Papers">
      <div className="bg-background border border-border rounded-lg p-5 md:p-6" data-testid="admin-user-purchases">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-5">
          <div>
            <h2 className="font-heading font-bold text-lg">Student paper access</h2>
            <p className="mt-1 text-sm text-muted-foreground">{users.length} users · {paperCount} distinct assigned or purchased papers</p>
          </div>
          <div className="flex gap-2">
            <div className="relative min-w-0 sm:w-72">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search user or paper" className="pl-9" data-testid="user-purchase-search" />
            </div>
            <Button variant="outline" size="icon" onClick={load} disabled={loading} aria-label="Refresh">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-14"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : filteredUsers.length === 0 ? (
          <p className="py-14 text-center text-sm text-muted-foreground">No matching users found.</p>
        ) : (
          <div className="space-y-3">
            {filteredUsers.map((user) => (
              <details key={user.id} className="group rounded-lg border border-border" data-testid={`admin-user-${user.id}`}>
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-4 hover:bg-muted/40">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{user.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}{user.phone ? ` · ${user.phone}` : ""}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-3 text-right">
                    <div><p className="text-sm font-semibold">{user.purchased_papers.length}</p><p className="text-xs text-muted-foreground">papers</p></div>
                    <Button type="button" size="sm" className="rounded-full gap-1.5" onClick={(event) => { event.preventDefault(); openAssignment(user); }} data-testid={`assign-paper-${user.id}`}>
                      <Plus className="h-3.5 w-3.5" /> Assign paper
                    </Button>
                    <span className="text-muted-foreground transition-transform group-open:rotate-180">⌄</span>
                  </div>
                </summary>
                <div className="border-t border-border p-4 overflow-x-auto">
                  {user.purchased_papers.length === 0 ? (
                    <p className="py-5 text-center text-sm text-muted-foreground">This user has no assigned or purchased question papers.</p>
                  ) : (
                    <Table>
                      <TableHeader><TableRow><TableHead>Question paper</TableHead><TableHead>Purchased</TableHead><TableHead>Assigned</TableHead><TableHead>Used</TableHead><TableHead>Remaining</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {user.purchased_papers.map((paper) => (
                          <TableRow key={paper.id}>
                            <TableCell><p className="font-medium">{paper.title}</p><p className="text-xs text-muted-foreground">{paper.subject}{paper.board ? ` · ${paper.board}` : ""}</p></TableCell>
                            <TableCell>{paper.purchased_quantity}</TableCell>
                            <TableCell>{paper.assigned_quantity}</TableCell>
                            <TableCell>{paper.attempts_used}</TableCell>
                            <TableCell className="font-semibold text-primary">{paper.remaining_attempts}</TableCell>
                            <TableCell className="text-right">
                              <Button size="sm" variant="destructive" className="rounded-full" onClick={() => setPending({ user, paper })} disabled={saving} data-testid={`delete-assignment-${user.id}-${paper.id}`}>Delete</Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </details>
            ))}
          </div>
        )}
      </div>

      <Dialog open={Boolean(assigningUser)} onOpenChange={(open) => { if (!open && !saving) setAssigningUser(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign a question paper</DialogTitle>
            <DialogDescription>
              Give {assigningUser?.name} access without a payment. The paper appears on the student dashboard immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Question paper</Label>
              <Select value={assignment.paperId} onValueChange={(paperId) => setAssignment((current) => ({ ...current, paperId }))}>
                <SelectTrigger data-testid="assignment-paper"><SelectValue placeholder="Select a paper" /></SelectTrigger>
                <SelectContent>
                  {papers.map((paper) => (
                    <SelectItem key={paper.id} value={paper.id}>
                      {paper.title} — {paper.subject}{paper.board ? ` · ${paper.board}` : ""}{paper.medium ? ` · ${paper.medium}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {papers.length === 0 && <p className="text-xs text-destructive">Create an active question paper with a PDF before assigning one.</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="assignment-quantity">Attempts</Label>
              <Input id="assignment-quantity" data-testid="assignment-quantity" type="number" min="1" max="50" value={assignment.quantity} onChange={(event) => setAssignment((current) => ({ ...current, quantity: event.target.value }))} />
              <p className="text-xs text-muted-foreground">Each attempt allows one answer-script submission for this paper.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssigningUser(null)} disabled={saving}>Cancel</Button>
            <Button onClick={assignPaper} disabled={saving || !assignment.paperId || Number(assignment.quantity) < 1 || Number(assignment.quantity) > 50} data-testid="confirm-paper-assignment">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Assign paper
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(pending)} onOpenChange={(open) => { if (!open && !saving) setPending(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this student’s paper access?</AlertDialogTitle>
            <AlertDialogDescription>This removes all purchased and assigned access to "{pending?.paper.title}" for {pending?.user.name}. Other papers, past submissions, and payment records are kept. This does not issue a refund.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(event) => { event.preventDefault(); deletePaperAccess(); }} disabled={saving} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Remove access</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardShell>
  );
}
